import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Form, Formik, Field } from 'formik';

import {
  onboardingBusinessTypeReskinRoute,
  onboardingLetsGetStartedRoute,
} from 'src/routing/routes';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { SalesforceContext, Steps } from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { requestSmsOtp, validateOtp } from 'src/services/otp-service';
import { OnboardingFormBackground } from '../../onboarding-form-background/onboarding-form-background';
import { OtpInput, ShapesDecorativeBorder } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import MiniAppEvents from 'src/helpers/miniapp-events';
import './agent-otp.scss';

export default function AgentOtp() {
  const otpLength = 4;
  const otpTriesMax = 3;
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const [otpTries, setOtpTries] = useState(0);
  const {
    lead,
    customerType,
    isLoadingFromSessionStorage,
    otpRetriesExceeded,
    setOtpRetriesExceeded,
  } = useContext(SalesforceContext);
  const otpTriesExceeded = () => otpTries >= otpTriesMax || otpRetriesExceeded;
  const handleTechnicalError = useTechnicalErrorHandler();
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const { save } = useContext(SalesforceContext);

  useEffect(() => {
    if (!isLoadingFromSessionStorage) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.continueApplicationOTPPageLoadFormStartCombined(customerType, assistantId);
    }
  }, [isLoadingFromSessionStorage]);

  useEffect(() => {
    if (otpRetriesExceeded) {
      const errorMsg = `You’ve exceeded the OTP attempts. Please try again later`;
      TealiumEvents.continueApplicationOTPEventError(errorMsg, customerType);
    }
  }, [otpRetriesExceeded]);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const submitForm = async (values, helpers) => {
    switch (values.submitAction) {
      case 'validate':
        await submitFormValidate(values, helpers);
        break;
      case 'request':
        await submitFormRequest(values, helpers);
        break;
    }
  };

  const submitFormRequest = async (values, helpers) => {
    const { setSubmitting, setFieldError, resetForm } = helpers;
    resetForm();
    TealiumEvents.continueApplicationOTPClickRequestNew(customerType);
    setOtpTries(0);

    setSubmitting(true);
    const { error } = await requestSmsOtp(lead.mobileNumber);

    let displayError = null;

    switch (error) {
      case null:
        toastMessageServiceSingleton.addToast({
          message: `We resent the customer an OTP via SMS`,
          type: 'info',
        });
        break;
      case 'Maximum retry limit exceeded. Locked for 24 hours.':
        setOtpRetriesExceeded(true);
        displayError = `You’ve exceeded the OTP attempts. Please try again later`;
        TealiumEvents.continueApplicationOTPEventError(displayError, customerType);
        break;
      default:
        handleTechnicalError(error, true);
        break;
    }

    if (displayError) {
      setFieldError('agentOtp', displayError);
    }

    setSubmitting(false);
  };

  const submitFormValidate = async (values, helpers) => {
    const { setSubmitting, setFieldError, setFieldValue } = helpers;

    setSubmitting(true);
    const { error } = await validateOtp(lead.mobileNumber, values.agentOtp);

    let displayError = null;

    switch (error) {
      case null:
        await save(Steps.LETS_GET_STARTED);
        setRedirectToRoute(onboardingBusinessTypeReskinRoute);
        break;
      case 'Maximum retry limit exceeded. Locked for 24 hours.':
        setOtpRetriesExceeded(true);
        displayError = `You’ve exceeded the OTP attempts. Please try again later`;
        TealiumEvents.continueApplicationOTPEventError(displayError, customerType);
        break;
      case 'Validation failed due to incorrect OTP':
        const otpRetriesCurrent = otpTries + 1;
        setOtpTries(otpRetriesCurrent);

        if (otpRetriesCurrent >= otpTriesMax) {
          setOtpRetriesExceeded(true);
          displayError = 'You’ve exceeded the OTP attempts. Please try again later';
          TealiumEvents.continueApplicationOTPEventError(displayError, customerType);
        } else {
          displayError = `Incorrect OTP - Try again ${otpRetriesCurrent}/${otpTriesMax}`;
          TealiumEvents.continueApplicationOTPEventError(displayError, customerType);
        }
        break;
      case 'No OTP found. Generate OTP first':
        setFieldValue('isOtpExpired', false, false);
        displayError = 'Your OTP has expired. Please request a new OTP';
        TealiumEvents.continueApplicationOTPEventError(displayError, customerType);
        break;
      default:
        handleTechnicalError('unrecognized error received in response to OTP get request');
        break;
    }

    if (displayError) {
      setFieldError('agentOtp', displayError);
    }

    setSubmitting(false);
  };

  const getInitialValues = () => {
    return {
      agentOtp: '',
      isOtpExpired: false,
      isRetryLimitExceeded: otpRetriesExceeded,
      submitAction: 'validate',
    };
  };

  const getInitialErrors = () => {
    const errors = {};

    if (otpRetriesExceeded) {
      errors.agentOtp = 'You’ve exceeded the OTP attempts. Please try again later';
    }

    return errors;
  };

  const backLink = () => (
    <div className="center-text back-link">
      <a
        onClick={() => {
          setRedirectToRoute(onboardingLetsGetStartedRoute);
        }}
        automationid="Onboarding_Agent_Otp_Anchor_Link_Back"
      >
        <span className="icon-menu-chevron-left has-padding-right-6  is-size-7" />
        Back
      </a>
    </div>
  );

  const form = () => (
    <section className="agent-otp">
      <Formik
        initialValues={getInitialValues()}
        initialErrors={getInitialErrors()}
        onSubmit={submitForm}
        validateOnChange={true}
      >
        {({ isSubmitting, values, errors, submitForm, setFieldValue }) => {
          const handleValidateOtp = async () => {
            setFieldValue('submitAction', 'validate');
            await submitForm();
          };

          const handleRequestOtp = async () => {
            if (!otpTriesExceeded()) {
              setFieldValue('submitAction', 'request');
              await submitForm();
            }
          };

          return (
            <Form>
              <section className="agent-otp-form">
                <div className="has-text-centered">
                  <div className="heading-we-sent-you-an-email">We sent the customer an OTP</div>

                  <p className="sub-heading-please-enter-otp">
                    Please enter the {otpLength} digit verification code to confirm that the
                    customer has accepted the terms and conditions.
                  </p>
                </div>

                <div className="otp-input-section" automationid="Onboarding_Agent_OTP_Div_Input">
                  <Field
                    name="agentOtp"
                    component={MiniAppFocusWrapper}
                    innerComponent={OtpInput}
                    key={otpTries}
                    shouldAutoFocus={true}
                    isDisabled={otpTriesExceeded() || values.isOtpExpired}
                    numInputs={otpLength}
                    autoFocus
                  />
                </div>

                <div className="continue-button-otp">
                  <button
                    onClick={handleValidateOtp}
                    automationid="Onboarding_Agent_OTP_Button_Submit_OTP"
                    disabled={
                      isSubmitting ||
                      values.agentOtp.toString().length < otpLength ||
                      errors.agentOtp ||
                      otpTriesExceeded()
                    }
                    className="button is-primary red-continue-button "
                  >
                    Continue
                  </button>
                </div>
                <div className="otp-not-received-yet">
                  <span className="request-reset-left">Didn't receive an OTP?</span>
                  &nbsp;
                  <span className="line-break">
                    <br />
                  </span>
                  <span
                    className={`request-new-otp ${otpTriesExceeded() ? 'otp-tries-exceeded' : ''}`}
                    onClick={handleRequestOtp}
                  >
                    Resend
                  </span>
                </div>
              </section>
              {!inMiniProgram && backLink()}
            </Form>
          );
        }}
      </Formik>
    </section>
  );
  return (
    <OnboardingFormBackground includeMiniAppBack={true}>
      <ShapesDecorativeBorder>{form()}</ShapesDecorativeBorder>
    </OnboardingFormBackground>
  );
}
